import { subMonths, subWeeks } from 'date-fns';
import { isEqual } from 'lodash';
import { action, computed, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    dateRange,
    DropdownMultiSelectFilter,
    Enums,
    humanizeString,
    IMultiSelectOption,
    inject,
    ModalHandle,
    NothingHere,
    SubTopBar,
    Title,
    toArray,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsBriefcase } from '@react-icons/all-files/bs/BsBriefcase';
import { RouteComponentProps } from 'react-router';
import { AccountsStore } from '../Accounts/AccountsStore';
import { BiStore, Labels } from '../BI/BiStore';
import { AppStore } from '../Core/AppStore';
import { DisplayFiltersStore } from '../DisplayFilters/DisplayFiltersStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { DisplayViewTypes } from '../DisplayFilters/DisplayViewSelector';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { DashboardDisplayList } from './DashboardDisplayList';
import { DashboardMap } from './DashboardMap';
import { DisplayListModal } from './DisplayListModal';
import { DisplayStatusCards } from './DisplaysStatusCards';
import { HotTicketsList } from './HotTicketsList';
import { ReportedEventsTable } from './ReportedEventsTable';
import {IWhereClause} from "../DisplayFilters/WhereFilter";

export interface IDashboardDateRange {
    name: string;
    startDate: Date;
    endDate: Date;
    format: string;
    groupByMonth: boolean;
}

export interface IDashboardProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class Dashboard extends React.Component<IDashboardProps> {
    public constructor(props: IDashboardProps) {
        super(props);

        this.biDisplayFilter.accounts = this.displayFiltersStore.storage.displayFilter.accounts?.slice();

        this.vm = new DisplayListViewModel({
            onChange: this.onChange,
            displayFilter: this.biDisplayFilter,
            displayEventFilter: this.displayListEventFilter,
            sort: ['StatusRank desc'],
            viewType: DisplayViewTypes.TableLarge,
            columns: [{ option: 'Status' }],
        });
    }

    @action
    public async componentDidMount() {
        this.biStore.dashboardDisplaysTitle = 'Total Displays';

        const storedOption = this.dateRangeOptions.find(o => o.name == this.biStore.storage.dateRangeOption);
        if (storedOption) {
            this.selectedDateRange = storedOption;
        }

        this.loading = true;
        try {
            const p1 = this.loadData();
            const p2 = this.accountsStore.loadAsync();
            await Promise.all([p1, p2]);
        } catch (e: any) {
            this.appStore.alertHandle.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="container">
                <Title>Dashboard</Title>

                <SubTopBar>
                    <ul className="nav d-flex justify-content-around">
                        {this.dateRangeOptions.map(o => (
                            <li key={o.name} className="nav-item text-primary">
                                <a className={this.dateRangeOptionClassName(o)} onClick={() => this.applyDateRange(o)}>
                                    {o.name}
                                </a>
                            </li>
                        ))}
                        {this.accountsStore.accounts.size > 1 && (
                            <li className="nav-item text-primary">
                                <div className="d-inline-block">
                                    <DropdownMultiSelectFilter
                                        title="All Accounts"
                                        options={toArray(this.accountsStore.accounts).map(a => {
                                            return { option: a };
                                        })}
                                        filter={this.vm.displayFilter.accounts?.map(a => {
                                            return { option: a };
                                        })}
                                        print={this.printAccount}
                                        sort={this.sortAccount}
                                        comparer={this.compareAccount}
                                        style={{
                                            minWidth: 400,
                                        }}
                                        className="border-top-0"
                                        onToggle={this.onAccountToggle}
                                        onChange={this.onAccountChange}
                                        caret
                                    />
                                </div>
                            </li>
                        )}
                    </ul>
                </SubTopBar>

                <NothingHere
                    empty={!!this.biStore.totals && !this.biStore.totals.total}
                    loading={this.loading}
                    icon={<BsBriefcase />}
                    label="No Data Available"
                    loadingMessage="Loading Dashboard..."
                />

                {!!this.biStore.totals && !!this.biStore.totals.total && (
                    <div className="row mb-3 justify-content-center">
                        <div className="col-md-6 col-xl-4 order-1 order-md-1 mb-3 px-2">
                            <DisplayStatusCards
                                loading={this.loading}
                                onClick={this.loadDisplaysForLabel}
                                vm={this.vm}
                            />
                        </div>

                        <div className="col-md-6 col-xl-4 order-4 order-md-2 mb-3 px-2">
                            <HotTicketsList loading={this.loading} />
                        </div>

                        <div className="col-md-6 col-xl-4 order-3 mb-3 px-2">
                            <ReportedEventsTable
                                loading={this.loading}
                                onClick={this.goToIncidentDisplays}
                                title={'Reported Events ' + this.selectedDateRange.name}
                            />
                        </div>

                        <div className="col-12 mb-3 px-2 order-2 order-md-4">
                            <DashboardDisplayList
                                loading={this.loading}
                                dateRange={this.dateRange}
                                selectedDateRange={this.selectedDateRange}
                                vm={this.vm}
                            />
                        </div>
                    </div>
                )}

                {this.displayListDisplayFilter && (
                    <DisplayListModal
                        displayEventFilter={this.displayListEventFilter}
                        displayFilter={this.displayListDisplayFilter}
                        where={this.displayListWhereClause}
                        title={this.displayListModalTitle}
                        modalHandle={this.displayListModalHandle}
                        history={this.props.history}
                        accounts={
                            this.biDisplayFilter.accounts
                                ? this.biDisplayFilter.accounts.map(a => a.accountName)
                                : undefined
                        }
                    />
                )}
            </div>
        );
    }

    @bind
    private applyDateRange(o: IDashboardDateRange) {
        this.setDateRange(o);
    }

    @bind
    @action
    private goToIncidentDisplays(type: Mri.DisplayApi.DowntimeIncidentTypes, displays: string[]) {
        this.displayListDisplayFilter = toJS(this.biDisplayFilter);
        this.displayListDisplayFilter.displays = displays;
        this.displayListEventFilter = undefined;
        this.displayListWhereClause = undefined;
        this.biStore.dashboardDisplaysTitle = humanizeString(Enums.toString(Enums.DowntimeIncidentType, type).slice(9));
        this.vm.where = [];
        this.vm.displayFilter = this.displayListDisplayFilter;

        this.loadData();
    }

    @bind
    @action
    private loadDisplaysForLabel(label: Labels) {
        this.displayListDisplayFilter = toJS(this.biDisplayFilter);
        this.displayListEventFilter = undefined;
        this.loading = true;
        this.biStore.dashboardDisplaysTitle = label;
        this.vm.where = [];

        switch (label) {
            default:
                this.displayListWhereClause = undefined;
                break;
            case Labels.Playing:
                this.displayListWhereClause =
                    '( ActiveDisplayStatus?.LastKnownPoll?.BacklightStatus!="Disabled" ) && ( ActiveDisplayStatus?.LastKnownPoll?.VideoStatus!="NotConnected" ) && ( ActiveDisplayStatus?.LastKnownPoll?.VideoStatus=="Valid" || ActiveDisplayStatus?.LastKnownPoll?.BacklightStatus=="StandbyMode" )';
                break;
            case Labels.Warning:
                this.displayListWhereClause =
                    'ActiveDisplayStatus?.DisplayStatus=="Online" && ( ActiveDisplayStatus?.LastKnownPoll?.DisplayHealth=="Warning" || ActiveDisplayStatus?.LastKnownPoll?.DisplayHealth=="Critical" )  && ActiveDisplayStatus?.LastKnownPoll?.BacklightStatus!="Disabled"';
                break;
            case Labels.NotPlaying:
                this.displayListWhereClause =
                    'ActiveDisplayStatus?.LastKnownPoll?.BacklightStatus=="Disabled" || ActiveDisplayStatus?.LastKnownPoll?.VideoStatus=="InternalError" || ActiveDisplayStatus?.LastKnownPoll?.VideoStatus=="InvalidResolution" || ActiveDisplayStatus?.LastKnownPoll?.VideoStatus=="NotConnected"';
                break;
            case Labels.TotalDisplays:
                this.displayListWhereClause = '';
                break;
        }
        if (!this.displayListWhereClause) {
            this.vm.where = [];
        } else {
            this.vm.where = [
                {
                    option: 'DisplayStatus',
                    value: this.displayListWhereClause,
                },
            ];
        }

        this.displayListModalTitle = `Displays that are ${label}`;
        this.loadData(true);
    }

    @bind
    @action
    private setDateRange(dateRangeOption: IDashboardDateRange) {
        this.selectedDateRange = dateRangeOption;
        this.biStore.storage.dateRangeOption = this.selectedDateRange.name;

        this.searchGuid = uuidv4generate();
        this.loadData();
    }

    @bind
    @action
    private async loadData(filterChange?: boolean) {
        this.loading = true;

        try {
            const p1 = this.biStore.loadTotalsAsync(this.biSearch);
            const p2 = this.biStore.loadDisplayEventTotalsAsync(this.biSearch);
            const p3 = this.biStore.loadStatusDateTotalsAsync(this.biSearch);
            const p4 = this.biStore.loadRuntimeStatisticsDateTotalsAsync(this.biSearch);
            const p5 = this.biStore.loadTicketsReportAsync(this.biSearch);
            const p6 = filterChange ? undefined : this.vm.loadMoreAsync();
            const p7 = filterChange ? undefined : this.vm.loadOptionsAsync();
            await Promise.all([p1, p2, p3, p4, p5, p6, p7]);
        } catch (e: any) {
            console.error(e);
        } finally {
            this.loading = false;
        }
    }

    @computed
    private get biSearch() {
        const search: Mri.Web.Portal.Controllers.BI.SearchModel = {
            searchGuid: this.searchGuid,
            displayFilter: this.biDisplayFilter,
            endDate: this.selectedDateRange.endDate,
            startDate: this.selectedDateRange.startDate,
            groupByMonth: this.selectedDateRange.groupByMonth,
        };

        return search;
    }

    @computed
    private get dateRange(): Date[] {
        const dates = dateRange(
            this.selectedDateRange.startDate,
            this.selectedDateRange.endDate,
            this.selectedDateRange.groupByMonth ? 'month' : 'day'
        );

        return Array.from(dates as any); // TODO a problem with Visual Studio is forcing this to be any?
    }

    @bind
    @action
    private onChange(
        displayFilter: Mri.Web.Portal.Controllers.DisplayFilters.DisplayFilterModel,
        displayEventFilter: Mri.DisplayEvents.DisplayEventFilter,
        sort: string[],
        where: IWhereClause[],
        columns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[]
    ) {
        //this.accountStore.storage.displayTableColumns = columns;
        this.displayFiltersStore.storage.displayFilter = displayFilter;
        this.displayFiltersStore.storage.sort = sort;
        this.displayFiltersStore.storage.where = where;
    }

    @bind
    private dateRangeOptionClassName(dateRangeOption: IDashboardDateRange) {
        let className = `btn btn-link nav-link`;
        if (isEqual(dateRangeOption, this.selectedDateRange)) {
            className += ' fw-bold stat-link active';
        }

        return className;
    }

    @bind
    @action
    private onAccountToggle(isOpen: boolean) {
        if (!isOpen) {
            this.loading = true;
            this.searchGuid = uuidv4generate();
            this.loadData();
        }
    }

    @bind
    @action
    private onAccountChange(value: IMultiSelectOption<Mri.DisplayFilters.Account>[] | null | undefined) {
        this.vm.displayFilter.accounts = value?.map(v => v.option) ?? [];
        this.displayFiltersStore.storage.displayFilter.accounts = value?.map(v => v.option) ?? [];
        this.biDisplayFilter.accounts = this.displayFiltersStore.storage.displayFilter.accounts;
    }

    private printAccount(entity: Mri.Accounts.Account) {
        return entity.accountName;
    }

    private sortAccount(a: IMultiSelectOption<Mri.Accounts.Account>, b: IMultiSelectOption<Mri.Accounts.Account>) {
        return a.option.accountName.localeCompare(b.option.accountName);
    }

    private compareAccount(a: Mri.Accounts.Account, b: Mri.Accounts.Account) {
        return a.accountNumber == b.accountNumber;
    }

    @observable
    private loading = false;

    @observable
    private biDisplayFilter: Mri.Web.Portal.Controllers.DisplayFilters.DisplayFilterModel = {};

    @observable
    private displayListModalHandle = new ModalHandle();
    @observable
    private displayListModalTitle?: React.ReactNode;
    @observable
    private displayListDisplayFilter?: Mri.Web.Portal.Controllers.DisplayFilters.DisplayFilterModel;
    @observable
    private displayListEventFilter?: Mri.DisplayEvents.DisplayEventFilter;
    @observable
    private displayListWhereClause?: string;
    @observable
    private vm: DisplayListViewModel;

    private readonly dateRangeOptions: IDashboardDateRange[] = [
        {
            name: 'Last Week',
            startDate: subWeeks(new Date(), 1),
            endDate: new Date(),
            format: 'M/d',
            groupByMonth: false,
        },
        {
            name: 'Last 2 Weeks',
            startDate: subWeeks(new Date(), 2),
            endDate: new Date(),
            format: 'M/d',

            groupByMonth: false,
        },
        {
            name: 'Last Month',
            startDate: subMonths(new Date(), 1),
            endDate: new Date(),
            format: 'M/d',
            groupByMonth: false,
        },
        //{
        //    name: 'Last 6 Months',
        //    startDate: subMonths(new Date(), 6),
        //    endDate: new Date(),
        //    format: 'MMMM',

        //    groupByMonth: true,
        //},
    ];

    @observable
    private selectedDateRange: IDashboardDateRange = this.dateRangeOptions[0];
    private searchGuid = uuidv4generate();
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private displayFiltersStore!: DisplayFiltersStore;
    @inject
    private biStore!: BiStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private appStore!: AppStore;
}
